import './LearningModule.scss';
import DOMPurify from 'dompurify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from 'index';
import { useParams, useSearchParams } from 'react-router-dom';
import { ModuleDetailsHeader } from '../ModuleDetails/ModuleDetailsHeader/ModuleDetailsHeader';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { TableOfContents } from 'pages/TableOfContents/TableOfContents';
import { GetPremiumCTA } from 'components/PremiumCTA/GetPremiumCTA';
import { useState, useEffect, useCallback } from 'react';
import { ContentsEntryType, LearningModuleLesson } from 'types/learningHub/moduleContents';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { CalculatorKind } from 'types/learningMaterials';
import { VideoLesson } from './TypeSpecificComponents/VideoLesson/VideoLesson';
import { CalculatorLesson } from './TypeSpecificComponents/CalculatorLesson/CalculatorLesson';
import { PictureLesson } from './TypeSpecificComponents/PictureLesson/PictureLesson';
import { TextLesson } from './TypeSpecificComponents/TextLesson/TextLesson';
import { ChapterTest } from './TypeSpecificComponents/ChapterTest/ChapterTest';
import { CertificationExam } from './TypeSpecificComponents/CertificationExam/CertificationExam';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import {
  getCertificationDescription,
  getInitialSelectedEntry,
  getLessonHeader,
  getSelectedCertification,
  getSelectedChapterTest,
  getSelectedLesson,
} from 'utilities/learningModuleHelpers';

export const LearningModule = () => {
  const [searchParams] = useSearchParams();
  const [selectedEntry, setSelectedEntry] = useState<{ entryId: string; type: ContentsEntryType; chapterId?: string } | undefined>(
    searchParams.get('entryId') && searchParams.get('type') ? { entryId: searchParams.get('entryId')!, type: searchParams.get('type')! as ContentsEntryType } : undefined
  );

  const slug = useParams().slug ?? '';
  const service = useServices().learningHubService;
  const profileService = useServices().profileService;
  const queryClient = useQueryClient();

  const { data: isUserMember, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    refetchOnWindowFocus: false,
  });

  const moduleDetails = useQuery({
    queryKey: ['moduleDetails', slug],
    queryFn: () => service.getModuleDetails(slug),
    enabled: !!slug,
    refetchOnWindowFocus: false,
  });

  const tableOfContents = useQuery({
    queryKey: ['moduleContents', moduleDetails.data?.id],
    queryFn: () => service.getModuleContents(moduleDetails.data?.id ?? ' '),
    enabled: !!moduleDetails.data?.id,
    refetchOnWindowFocus: false,
  });

  const markResourceAsDone = useMutation({
    mutationFn: (contentId: string) => service.markResourceAsDone(contentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['moduleContents', moduleDetails.data?.id] });
    },
  });

  useEffect(() => {
    if (tableOfContents.data && !selectedEntry) {
      const { entryId, type, chapterId, isCompleted } = getInitialSelectedEntry(tableOfContents.data?.chapters ?? []);
      setSelectedEntry({ entryId, type, chapterId });
      if (!isCompleted) {
        markResourceAsDone.mutate(entryId);
      }
    }
  }, [tableOfContents.data]);

  const selectEntry = useCallback(
    (entryId: string, type: ContentsEntryType, isCompleted: boolean, chapterId?: string) => {
      setSelectedEntry({ entryId, type, chapterId });

      if (!isCompleted && type === 'lesson' && !markResourceAsDone.isPending) {
        markResourceAsDone.mutate(entryId);
      }
    },
    [markResourceAsDone]
  );

  const isViewLoaded = !moduleDetails.isPending && moduleDetails.data && !tableOfContents.isPending && tableOfContents.data;

  return (
    <>
      <ModuleDetailsHeader
        title={moduleDetails?.data?.title}
        shortDescription={moduleDetails?.data?.shortDescription}
        navigationPath={`/LearningHub/Modules/${slug}`}
        isPending={moduleDetails.isPending}
      ></ModuleDetailsHeader>

      <div className='learning-module-wrapper'>
        <div className='contents'>
          {isViewLoaded ? (
            <TableOfContents
              contents={tableOfContents?.data}
              isInteractive={true}
              customClasses='contents-in-module'
              selectedEntryId={selectedEntry?.entryId}
              handleEntrySelection={(id, type, isCompleted, chapterId) => selectEntry(id, type, isCompleted, chapterId)}
            />
          ) : (
            <Skeleton width='100%' height='100%' variant='rectangular' />
          )}
        </div>
        <div className='learning-module-content'>
          {isViewLoaded && selectedEntry ? (
            <>
              <div className='title-wrapper'>
                {selectedEntry.type === 'lesson' && (
                  <>
                    <Subtitle>
                      <>
                        {
                          getLessonHeader(
                            tableOfContents?.data.chapters ?? [],
                            selectedEntry?.chapterId ?? '',
                            selectedEntry?.entryId ?? ''
                          ).header
                        }
                      </>
                    </Subtitle>
                    <Paragraph>
                      <>
                        {
                          getLessonHeader(
                            tableOfContents?.data.chapters ?? [],
                            selectedEntry?.chapterId ?? '',
                            selectedEntry?.entryId ?? ''
                          ).subheader
                        }
                      </>
                    </Paragraph>
                  </>
                )}
                {selectedEntry.type === 'certificate' && (
                  <>
                    <p className='subtitle'>Certification exam</p>
                    <div className='certificate-title'>
                      {isUserMember ? (
                        getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                        'Completed' ? (
                          <></>
                        ) : (
                          <Medal />
                        )
                      ) : (
                        <Crown className='premium-test' />
                      )}
                      <Subtitle>
                        <>{getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.title}</>
                      </Subtitle>
                    </div>
                    {getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                    'Completed' ? (
                      <></>
                    ) : (
                      <Paragraph>
                        <>{getCertificationDescription(moduleDetails.data?.certificationTests ?? [], selectedEntry?.entryId ?? '')}</>
                      </Paragraph>
                    )}
                  </>
                )}
                {selectedEntry.type === 'chapterTest' && (
                  <>
                    <p className='subtitle'>Chapter test</p>
                    <div className='certificate-title'>
                      {isUserMember ? <></> : <Crown className='premium-test' />}
                      <Subtitle>
                        <>{getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.title}</>
                      </Subtitle>
                    </div>
                  </>
                )}
              </div>
              <div>
                {selectedEntry?.type === 'chapterTest' && (
                  <ChapterTest
                    chapterTestId={getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.id ?? ''}
                    moduleId={moduleDetails.data?.id ?? ''}
                    isCompleted={
                      getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.status === 'Completed'
                    }
                  />
                )}
                {selectedEntry?.type === 'certificate' && (
                  <CertificationExam
                    isCompleted={
                      getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                      'Completed'
                    }
                    title={getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.title ?? ''}
                    certificationId={
                      getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.id ?? ''
                    }
                    moduleId={moduleDetails.data?.id ?? ''}
                    progress={
                      getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.progress ?? 0
                    }
                    badgeImageUrl={
                      moduleDetails.data?.certificationTests.find((ct) => ct.id === selectedEntry?.entryId)?.badgeImageUrl ?? ''
                    }
                  />
                )}
                {selectedEntry?.type === 'lesson' && (
                  <LessonComponent
                    content={getSelectedLesson(
                      tableOfContents.data?.chapters ?? [],
                      selectedEntry?.chapterId ?? '',
                      selectedEntry?.entryId ?? ''
                    )}
                  />
                )}
              </div>
              {!isPending && !isUserMember && <GetPremiumCTA />}
            </>
          ) : (
            <>
              <Skeleton width={150} height={30} style={{ marginBottom: 20 }} variant='rounded' />
              <Skeleton width='100%' height='400px' variant='rounded' />
            </>
          )}
        </div>
      </div>
    </>
  );
};

type LessonProps = {
  content: LearningModuleLesson | undefined;
};

const LessonComponent = ({ content }: LessonProps) => {
  return (
    <>
      {content?.kind === 'Video' && <VideoLesson youTubeKey={content.youTubeKey ?? ''} title={content.title} />}
      {content?.kind === 'Calculator' && (
        <CalculatorLesson
          calculatorKind={content.calculatorKind as CalculatorKind}
          calculatorOptions={JSON.parse(content.calculatorOptions ?? '')}
        />
      )}
      {content?.kind === 'Text' && <TextLesson text={content.text ?? ''} />}
      {content?.kind === 'Picture' && (
        <div className='picture-lesson'>
          <PictureLesson imageUrl={(content.medias && content.medias[0]?.fileUrl) ?? ''} imageTitle={content.title} />
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.text ?? '', { ADD_ATTR: ['target'] }) }}></div>
        </div>
      )}
    </>
  );
};
